import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { signOut } from "firebase/auth";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useVisionUIController, setLayout } from "context";
import { auth } from "../../firebase";
import {
  getVisitorProfile,
  getVisitorCs01List,
  getVisitorCs01Fee,
  createVisitorCs01,
  createPaymentIntent,
  confirmVisitorPayment,
  submitVisitorCs01,
} from "services/visitorApiService";
import { TC } from "../../tokens/text";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const STATUS_COLORS = {
  draft: "#ff9800",
  submitted: "#4caf50",
};

function PaymentForm({ csId, feePence, onPaid, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const handlePay = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setPaying(true);
    setPayError("");
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setPayError(error.message || "Payment failed.");
      setPaying(false);
      return;
    }

    if (!paymentIntent?.id) {
      setPayError("Payment completed but confirmation details were missing.");
      setPaying(false);
      return;
    }

    try {
      await confirmVisitorPayment(csId, { paymentIntentId: paymentIntent.id, amount: feePence });
      onPaid();
    } catch (confirmError) {
      setPayError(confirmError.message || "Could not confirm payment.");
      setPaying(false);
    }
  };

  return (
    <VuiBox component="form" onSubmit={handlePay}>
      <VuiTypography sx={{ color: "#a0aec0", fontSize: 14, mb: 2 }}>
        A fee of <strong>{`\u00A3${(feePence / 100).toFixed(2)}`}</strong> is required to submit your CS01.
      </VuiTypography>
      <PaymentElement />
      {payError && <VuiTypography sx={{ color: "#ef5350", fontSize: 13, mt: 1 }}>{payError}</VuiTypography>}
      <VuiBox display="flex" gap={2} mt={3}>
        <VuiButton variant="outlined" color="secondary" onClick={onBack} disabled={paying}>
          Back
        </VuiButton>
        <VuiButton type="submit" color="info" disabled={!stripe || paying}>
          {paying ? "Processing..." : `Pay \u00A3${(feePence / 100).toFixed(2)}`}
        </VuiButton>
      </VuiBox>
    </VuiBox>
  );
}

export default function VisitorPortal() {
  const history = useHistory();
  const [, dispatch] = useVisionUIController();
  const [profile, setProfile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [fee, setFee] = useState({ feePence: 10000, feePounds: "100.00" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [csDetails, setCsDetails] = useState({
    madeUpToDate: "",
    confirmationDate: "",
    registeredAddress: "",
    sicCodes: "",
    officers: "",
  });
  const [currentCsId, setCurrentCsId] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setLayout(dispatch, "page");
  }, [dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [profileResult, listResult, feeResult] = await Promise.all([
        getVisitorProfile(),
        getVisitorCs01List(),
        getVisitorCs01Fee(),
      ]);
      setProfile(profileResult);
      setSubmissions(listResult.submissions || []);
      setFee(feeResult);
      setCsDetails((prev) => ({
        ...prev,
        madeUpToDate: profileResult.companyData?.confirmationStatementLastMadeUpTo || profileResult.companyData?.lastAccountsMadeUpTo || "",
        confirmationDate: profileResult.companyData?.confirmationStatementNextDue || "",
        registeredAddress: profileResult.companyData?.registeredAddress || "",
        sicCodes: Array.isArray(profileResult.companyData?.sicCodes) ? profileResult.companyData.sicCodes.join(", ") : "",
      }));
    } catch (loadError) {
      setError(loadError.message || "Failed to load portal.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSignOut = async () => {
    await signOut(auth);
    history.push("/authentication/sign-in");
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setStep(0);
    setCurrentCsId(null);
    setClientSecret("");
    setSubmitError("");
    setSubmitLoading(false);
  };

  const handleStartCs01 = () => {
    setWizardOpen(true);
    setStep(0);
    setCurrentCsId(null);
    setClientSecret("");
    setSubmitError("");
  };

  const handleStep1Next = async () => {
    setSubmitLoading(true);
    setSubmitError("");
    try {
      const submission = await createVisitorCs01({
        madeUpToDate: csDetails.madeUpToDate,
        confirmationDate: csDetails.confirmationDate,
        registeredAddress: csDetails.registeredAddress,
        sicCodes: csDetails.sicCodes.split(",").map((item) => item.trim()).filter(Boolean),
        officers: csDetails.officers ? csDetails.officers.split(",").map((item) => item.trim()).filter(Boolean) : [],
      });
      setCurrentCsId(submission.id);
      const intent = await createPaymentIntent(submission.id);
      setClientSecret(intent.clientSecret);
      setStep(1);
    } catch (stepError) {
      setSubmitError(stepError.message || "Could not prepare CS01.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePaid = () => {
    setSubmitError("");
    setStep(2);
  };

  const handleFinalSubmit = async () => {
    setSubmitLoading(true);
    setSubmitError("");
    try {
      await submitVisitorCs01(currentCsId);
      setStep(3);
      await load();
    } catch (finalError) {
      setSubmitError(finalError.message || "Could not submit CS01.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const companyStatus = useMemo(
    () => profile?.companyData?.companyStatus || profile?.companyData?.status || "-",
    [profile]
  );

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: TC.linkHover }} />
      </VuiBox>
    );
  }

  return (
    <VuiBox minHeight="100vh" sx={{ background: "linear-gradient(135deg, #0f1535 0%, #111c44 100%)", p: { xs: 2, md: 4 } }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={4} gap={2} flexWrap="wrap">
        <VuiBox>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            CS01 Self-Service Portal
          </VuiTypography>
          {profile && (
            <VuiTypography sx={{ color: "#a0aec0", fontSize: 14, mt: 0.5 }}>
              {profile.companyName} {"\u00B7"} {profile.chNumber}
            </VuiTypography>
          )}
        </VuiBox>
        <VuiButton variant="outlined" color="secondary" size="small" onClick={handleSignOut}>
          Sign Out
        </VuiButton>
      </VuiBox>

      {error && <VuiTypography sx={{ color: "#ef5350", mb: 2 }}>{error}</VuiTypography>}

      {profile && (
        <VuiBox sx={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, p: 3, mb: 4, border: "1px solid rgba(255,255,255,0.1)" }}>
          <VuiTypography color="white" fontWeight="bold" sx={{ mb: 2 }}>
            Your Company
          </VuiTypography>
          <VuiBox display="flex" flexWrap="wrap" gap={3}>
            <VuiBox>
              <VuiTypography sx={{ color: "#a0aec0", fontSize: 12 }}>Company Name</VuiTypography>
              <VuiTypography color="white" fontWeight="medium">{profile.companyName}</VuiTypography>
            </VuiBox>
            <VuiBox>
              <VuiTypography sx={{ color: "#a0aec0", fontSize: 12 }}>CH Number</VuiTypography>
              <VuiTypography color="white" fontWeight="medium">{profile.chNumber}</VuiTypography>
            </VuiBox>
            <VuiBox>
              <VuiTypography sx={{ color: "#a0aec0", fontSize: 12 }}>Status</VuiTypography>
              <VuiTypography color="white" fontWeight="medium">{companyStatus}</VuiTypography>
            </VuiBox>
            <VuiBox>
              <VuiTypography sx={{ color: "#a0aec0", fontSize: 12 }}>Registered Address</VuiTypography>
              <VuiTypography color="white" fontWeight="medium" sx={{ maxWidth: 360 }}>
                {profile.companyData?.registeredAddress || "-"}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      )}

      <VuiBox sx={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, p: 3, mb: 4, border: "1px solid rgba(255,255,255,0.1)" }}>
        <VuiTypography color="white" fontWeight="bold" sx={{ mb: 1 }}>
          Current CS01 Fee
        </VuiTypography>
        <VuiTypography sx={{ color: "#a0aec0", fontSize: 14 }}>
          The current portal fee is <strong>{`\u00A3${fee.feePounds}`}</strong> per submission.
        </VuiTypography>
      </VuiBox>

      <VuiBox sx={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, p: 3, mb: 4, border: "1px solid rgba(255,255,255,0.1)" }}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
          <VuiTypography color="white" fontWeight="bold">CS01 Submissions</VuiTypography>
          <VuiButton color="info" size="small" onClick={handleStartCs01}>+ New CS01</VuiButton>
        </VuiBox>
        {submissions.length === 0 ? (
          <VuiTypography sx={{ color: "#a0aec0", fontSize: 14 }}>
            No submissions yet. Click "New CS01" to get started.
          </VuiTypography>
        ) : (
          <VuiBox>
            {submissions.map((submission) => (
              <VuiBox
                key={submission.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 2, mb: 1, borderRadius: 2, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", gap: 2, flexWrap: "wrap" }}
              >
                <VuiBox>
                  <VuiTypography color="white" fontWeight="medium" sx={{ fontSize: 14 }}>
                    {submission.companyName}
                  </VuiTypography>
                  <VuiTypography sx={{ color: "#a0aec0", fontSize: 12 }}>
                    Made up to: {submission.madeUpToDate || "-"} {"\u00B7"} Created: {new Date(submission.createdAt).toLocaleDateString("en-GB")}
                  </VuiTypography>
                </VuiBox>
                <VuiBox display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={submission.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                    size="small"
                    sx={{ fontSize: 11, background: submission.paymentStatus === "paid" ? "rgba(76,175,80,0.2)" : "rgba(255,152,0,0.2)", color: submission.paymentStatus === "paid" ? "#4caf50" : "#ff9800", border: "none" }}
                  />
                  <Chip
                    label={submission.status}
                    size="small"
                    sx={{ fontSize: 11, background: `${STATUS_COLORS[submission.status] || "#607d8b"}22`, color: STATUS_COLORS[submission.status] || "#607d8b", border: "none", textTransform: "capitalize" }}
                  />
                </VuiBox>
              </VuiBox>
            ))}
          </VuiBox>
        )}
      </VuiBox>

      {wizardOpen && (
        <VuiBox sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <VuiBox sx={{ background: "#1a2035", borderRadius: 3, p: 4, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(255,255,255,0.1)" }}>
            <VuiTypography color="white" fontWeight="bold" sx={{ mb: 3, fontSize: 18 }}>
              Submit CS01 Confirmation Statement
            </VuiTypography>

            <Stepper activeStep={Math.min(step, 2)} sx={{ mb: 4, "& .MuiStepLabel-label": { color: "#a0aec0", fontSize: 12 }, "& .MuiStepLabel-label.Mui-active": { color: "#fff" }, "& .MuiStepIcon-root": { color: "#2a3a5c" }, "& .MuiStepIcon-root.Mui-active": { color: TC.linkHover }, "& .MuiStepIcon-root.Mui-completed": { color: "#4caf50" } }}>
              {["Company Details", "Payment", "Submit"].map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {step === 0 && (
              <VuiBox>
                {[
                  { label: "Made-Up-To Date", field: "madeUpToDate", type: "date" },
                  { label: "Confirmation Date", field: "confirmationDate", type: "date" },
                  { label: "Registered Address", field: "registeredAddress", type: "text" },
                  { label: "SIC Codes (comma separated)", field: "sicCodes", type: "text" },
                ].map(({ label, field, type }) => (
                  <VuiBox key={field} mb={2}>
                    <VuiTypography sx={{ color: "#a0aec0", fontSize: 12, mb: 0.5 }}>{label}</VuiTypography>
                    <TextField
                      fullWidth
                      type={type}
                      size="small"
                      value={csDetails[field]}
                      onChange={(event) => setCsDetails((prev) => ({ ...prev, [field]: event.target.value }))}
                      InputLabelProps={type === "date" ? { shrink: true } : undefined}
                      multiline={field === "registeredAddress"}
                      minRows={field === "registeredAddress" ? 2 : undefined}
                      sx={{ "& .MuiOutlinedInput-root": { color: "#fff", "& fieldset": { borderColor: "rgba(255,255,255,0.2)" } }, "& input": { color: "#fff" }, "& textarea": { color: "#fff" } }}
                    />
                  </VuiBox>
                ))}
                {submitError && <VuiTypography sx={{ color: "#ef5350", fontSize: 13, mb: 1 }}>{submitError}</VuiTypography>}
                <VuiBox display="flex" gap={2} mt={2}>
                  <VuiButton variant="outlined" color="secondary" onClick={handleCloseWizard}>Cancel</VuiButton>
                  <VuiButton color="info" onClick={handleStep1Next} disabled={submitLoading || !csDetails.madeUpToDate}>
                    {submitLoading ? "Saving..." : "Next: Payment"}
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            )}

            {step === 1 && clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "night" } }}>
                <PaymentForm csId={currentCsId} feePence={fee.feePence} onPaid={handlePaid} onBack={() => setStep(0)} />
              </Elements>
            )}

            {step === 2 && (
              <VuiBox>
                <VuiTypography sx={{ color: "#a0aec0", fontSize: 14, mb: 3 }}>
                  Payment confirmed. Click "Submit CS01" to finalise your Confirmation Statement.
                </VuiTypography>
                {submitError && <VuiTypography sx={{ color: "#ef5350", fontSize: 13, mb: 1 }}>{submitError}</VuiTypography>}
                <VuiBox display="flex" gap={2}>
                  <VuiButton color="info" onClick={handleFinalSubmit} disabled={submitLoading}>
                    {submitLoading ? "Submitting..." : "Submit CS01"}
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            )}

            {step === 3 && (
              <VuiBox textAlign="center">
                <VuiTypography sx={{ color: "#4caf50", fontSize: 48 }}>[OK]</VuiTypography>
                <VuiTypography color="white" fontWeight="bold" sx={{ fontSize: 18, mb: 1 }}>
                  CS01 Submitted!
                </VuiTypography>
                <VuiTypography sx={{ color: "#a0aec0", fontSize: 14, mb: 3 }}>
                  Your Confirmation Statement has been successfully submitted.
                </VuiTypography>
                <VuiButton color="info" onClick={handleCloseWizard}>Close</VuiButton>
              </VuiBox>
            )}
          </VuiBox>
        </VuiBox>
      )}
    </VuiBox>
  );
}

